import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ViewType } from './types';
// Import icons directly from lucide-react for the Switcher to ensure perfect match with MainSidebar
import { MonitorPlay, LogOut, LogIn } from 'lucide-react';
// Import existing AiQ icons
import {
  IconDashboard, IconQuant, IconRisk, IconOps, IconIPV,
  IconSearch, IconMarketData, IconNews, IconStrategy, IconMenu,
  IconCommand, IconBell, IconReport, IconShield, IconStamp, IconBank,
  IconTarget, IconCode, IconScale, IconBuilding, IconBriefcase, IconFlow,
  IconChartPie, IconFilter, IconLayers, IconFileUp, IconChevronDown,
  IconChevronRight, IconCube, IconGlobeAlt, IconEyeOff, IconSettings,
  IconPresentationChart, IconPenTool, IconUserGroup, IconChat, IconActivity,
  IconCalendar, IconBarChart, IconSliders, IconShieldCheck
} from './components/Icons';

// --- View Imports ---
import DashboardView from './views/DashboardView';

// Retail Investor Views (Migrated)
import MyPortfolioView from './views/MyPortfolioView';
import TransactionsView from './views/TransactionsView';
import WatchlistView from './views/WatchlistView';
import StockAnalysisView from './views/StockAnalysisView';
import NewsIntelligenceView from './views/NewsIntelligenceView';
import AiChatView from './views/AiChatView';
import BacktestPairsView from './views/BacktestPairsView';
import CommunityHubView from './views/CommunityHubView';
import LiveChatView from './views/LiveChatView';

// Institutional Views
import QuantPricingView from './views/QuantPricingView';
import ArchitectureView from './views/ArchitectureView';
import RiskManagementView from './views/RiskManagementView';
import MultiAssetOpsView from './views/MultiAssetOpsView';
import IPVControlView from './views/IPVControlView';
import MarketDataView from './views/MarketDataView';
import StrategyLabView from './views/StrategyLabView';
import ReportingView from './views/ReportingView';
import CollateralView from './views/CollateralView';
import RegulatoryView from './views/RegulatoryView';
import TreasuryView from './views/TreasuryView';
import RFQView from './views/RFQView';
import AlgoView from './views/AlgoView';
import ReconciliationView from './views/ReconciliationView';
import CounterpartyView from './views/CounterpartyView';
import WorkflowView from './views/WorkflowView';
import AnalyticsView from './views/AnalyticsView';
import MarketScreenerView from './views/MarketScreenerView';
import ProgramTradingView from './views/ProgramTradingView';
import DocumentIntelligenceView from './views/DocumentIntelligenceView';
import ModelRegistryView from './views/ModelRegistryView';
import MacroView from './views/MacroView';
import MarketBreadthView from './views/MarketBreadthView';
import SurveillanceView from './views/SurveillanceView';
import AdminView from './views/AdminView';
import TCAView from './views/TCAView';
import PortfolioView from './views/PortfolioView';
import StructuringView from './views/StructuringView';
import CRMView from './views/CRMView';
import CollaborationView from './views/CollaborationView';

// New Research Tools
import ValuationView from './views/ValuationView';
import EarningsCalendarView from './views/EarningsCalendarView';
import FactorExposureView from './views/FactorExposureView';

// New Quant Lab Tools
import KellySizingView from './views/KellySizingView';

// Market Insights
import InsightsView from './views/InsightsView';
import PublicInsightsView from './views/PublicInsightsView';
import InsightTrackRecordView from './views/InsightTrackRecordView';

// HK Regulatory (Type 11 SFC)
import CounterpartyMonitorView from './views/CounterpartyMonitorView';
import MarginDashboardView from './views/MarginDashboardView';
import CapitalAdequacyView from './views/CapitalAdequacyView';
import SFCReportingView from './views/SFCReportingView';

// Gate screens for access control
import { LoginGate, BYOKGate } from './components/GateScreen';

// --- Component Imports ---
import AiQAssistant from './components/AiQAssistant';
import LiveTicker from './components/LiveTicker';
import CommandPalette from './components/CommandPalette';
import NotificationPanel from './components/NotificationPanel';
import ProfileModal from './components/ProfileModal';
import DemoBanner from '../components/DemoBanner';
import { Toaster, toast } from 'react-hot-toast';
import { AiQProvider, useAiQ } from './state/AiQState';
import { useAuth } from '../context/AuthContext'; // Import Auth Context for Logout
import { usePortfolio } from '../context/PortfolioContext'; // Import Portfolio Context
import { useDemo } from '../context/DemoContext'; // Import Demo Context
// @ts-ignore
import PortfolioSwitcher from '../components/PortfolioSwitcher';

// API URL for backend calls
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Admin emails — these users can access Market Insights (generate/edit)
const ADMIN_EMAILS = ['dbai999@talkoru.com', 'dbai1089@talkoru.com'];

// --- Menu Structure ---
interface MenuItem { id: ViewType; label: string; icon: React.ReactNode; adminOnly?: boolean; }
interface MenuGroup { label: string; sublabel: string; color: string; items: MenuItem[]; }

const MENU_STRUCTURE: MenuGroup[] = [
    {
        label: "Front Desk",
        sublabel: "Client & Portfolio",
        color: "#3B82F6", // blue
        items: [
            { id: ViewType.MY_PORTFOLIO, label: 'Portfolio Overview', icon: <IconBriefcase /> },
            { id: ViewType.WATCHLIST, label: 'Watchlist', icon: <IconFilter /> },
            { id: ViewType.TRANSACTIONS, label: 'Order Blotter', icon: <IconOps /> },
            { id: ViewType.RFQ_HUB, label: 'Smart Execution', icon: <IconTarget /> },
            { id: ViewType.CRM, label: 'Investor Relations', icon: <IconUserGroup /> },
        ]
    },
    {
        label: "Research",
        sublabel: "Market Intelligence",
        color: "#F59E0B", // amber
        items: [
            { id: ViewType.STOCK_ANALYSIS, label: 'Stock Analysis', icon: <IconSearch /> },
            { id: ViewType.NEWS_INTELLIGENCE, label: 'News Intelligence', icon: <IconNews /> },
            { id: ViewType.MACRO, label: 'Macro Desk', icon: <IconGlobeAlt /> },
            { id: ViewType.MARKET_BREADTH, label: 'Market Breadth', icon: <IconActivity /> },
            { id: ViewType.SCREENER, label: 'Market Scanner', icon: <IconFilter /> },
            { id: ViewType.VALUATION, label: 'DCF Valuation', icon: <IconTarget /> },
            { id: ViewType.EARNINGS_CALENDAR, label: 'Earnings Calendar', icon: <IconCalendar /> },
            { id: ViewType.FACTOR_EXPOSURE, label: 'Factor Exposure', icon: <IconBarChart /> },
            { id: ViewType.AI_CHAT, label: 'AI Analyst', icon: <IconChat /> },
            { id: ViewType.MARKET_INSIGHTS, label: 'Market Insights', icon: <IconPenTool />, adminOnly: true },
            { id: ViewType.PUBLIC_INSIGHTS, label: 'Published Insights', icon: <IconReport /> },
            { id: ViewType.INSIGHT_TRACK_RECORD, label: 'Insight Track Record', icon: <IconActivity /> },
        ]
    },
    {
        label: "Quant Lab",
        sublabel: "Strategy & Algo",
        color: "#8B5CF6", // violet
        items: [
            { id: ViewType.STRATEGY_LAB, label: 'Strategy Lab', icon: <IconStrategy /> },
            { id: ViewType.BACKTEST_PAIRS, label: 'Backtesting', icon: <IconPresentationChart /> },
            { id: ViewType.PROGRAM_TRADING, label: 'Program Trading', icon: <IconLayers /> },
            { id: ViewType.STRUCTURING, label: 'Structuring Desk', icon: <IconPenTool /> },
            { id: ViewType.ALGO_STUDIO, label: 'Algo Studio', icon: <IconCode /> },
            { id: ViewType.KELLY_SIZING, label: 'Position Sizing', icon: <IconSliders /> },
            { id: ViewType.QUANT_COMPUTING, label: 'Quant Pricing', icon: <IconQuant /> },
            { id: ViewType.MODEL_REGISTRY, label: 'Model Registry', icon: <IconCube /> },
        ]
    },
    {
        label: "Risk Management",
        sublabel: "Exposure & Control",
        color: "#EF4444", // red
        items: [
            { id: ViewType.RISK_MANAGEMENT, label: 'Risk Dashboard', icon: <IconRisk /> },
            { id: ViewType.COUNTERPARTY, label: 'Counterparty 360', icon: <IconBuilding /> },
            { id: ViewType.IPV_CONTROL, label: 'IPV & Valuation', icon: <IconIPV /> },
            { id: ViewType.COLLATERAL, label: 'Collateral & Margin', icon: <IconShield /> },
            { id: ViewType.TCA, label: 'TCA & Performance', icon: <IconPresentationChart /> },
        ]
    },
    {
        label: "Middle Office",
        sublabel: "Analytics & Data",
        color: "#06B6D4", // cyan
        items: [
            { id: ViewType.ANALYTICS, label: 'Analytics Workbench', icon: <IconChartPie /> },
            { id: ViewType.MARKET_DATA, label: 'Market Data', icon: <IconMarketData /> },
            { id: ViewType.COMPLIANCE, label: 'Compliance Monitor', icon: <IconEyeOff /> },
        ]
    },
    {
        label: "Back Office",
        sublabel: "Operations & Reporting",
        color: "#64748B", // slate
        items: [
            { id: ViewType.MULTI_ASSET_OPS, label: 'Operations Blotter', icon: <IconOps /> },
            { id: ViewType.RECONCILIATION, label: 'Reconciliation', icon: <IconScale /> },
            { id: ViewType.TREASURY, label: 'Treasury & Liquidity', icon: <IconBank /> },
            { id: ViewType.REGULATORY, label: 'Regulatory Reporting', icon: <IconStamp /> },
            { id: ViewType.WORKFLOW, label: 'Workflow Monitor', icon: <IconFlow /> },
            { id: ViewType.REPORTING, label: 'Reporting Center', icon: <IconReport /> },
        ]
    },
    {
        label: "Community",
        sublabel: "Collaboration",
        color: "#10B981", // emerald
        items: [
            { id: ViewType.COMMUNITY_HUB, label: 'Discussion Board', icon: <IconUserGroup /> },
            { id: ViewType.LIVE_CHAT, label: 'Live Chat', icon: <IconChat /> },
            { id: ViewType.COLLABORATION, label: 'Team Workspace', icon: <IconChat /> },
            { id: ViewType.DOC_INTELLIGENCE, label: 'Document Digitizer', icon: <IconFileUp /> },
        ]
    },
    {
        label: "HK Regulatory",
        sublabel: "Type 11 — SFC Compliance",
        color: "#F43F5E", // rose
        items: [
            { id: ViewType.COUNTERPARTY_MONITOR, label: 'Counterparty Monitor', icon: <IconBuilding /> },
            { id: ViewType.MARGIN_DASHBOARD, label: 'Margin Dashboard', icon: <IconScale /> },
            { id: ViewType.CAPITAL_ADEQUACY, label: 'Capital Adequacy', icon: <IconShieldCheck /> },
            { id: ViewType.SFC_REPORTING, label: 'SFC Reporting', icon: <IconStamp /> },
        ]
    },
    {
        label: "Administration",
        sublabel: "System",
        color: "#6B7280", // gray
        items: [
            { id: ViewType.ADMIN, label: 'Admin Console', icon: <IconSettings /> },
        ]
    }
];

// ── Access Control ────────────────────────────────────────────────────────────
// 'public'  → no login required (math tools, market data, demo views)
// 'auth'    → login required   (personal portfolio, watchlist, community)
// 'byok'    → API key required  (AI analyst, AI-powered news)
type AccessLevel = 'public' | 'auth' | 'byok';

const VIEW_ACCESS: Partial<Record<ViewType, AccessLevel>> = {
  // ── Auth required (personal data) ──
  [ViewType.DASHBOARD]:       'auth',
  [ViewType.MY_PORTFOLIO]:    'auth',
  [ViewType.WATCHLIST]:       'auth',
  [ViewType.TRANSACTIONS]:    'auth',
  [ViewType.COMMUNITY_HUB]:  'auth',
  [ViewType.LIVE_CHAT]:       'auth',
  [ViewType.ADMIN]:           'auth',

  // ── BYOK required (AI features) ──
  [ViewType.AI_CHAT]:            'byok',
  [ViewType.NEWS_INTELLIGENCE]:  'byok',

  // Everything else defaults to 'public' (omitted from map)
};

// ── Market session indicator ───────────────────────────────────────────────────
function MarketSessionBadge() {
  const now = new Date();
  const utcHour = now.getUTCHours();
  const utcMin = now.getUTCMinutes();
  const totalMin = utcHour * 60 + utcMin;
  const dayOfWeek = now.getUTCDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  let session = { label: 'After Hours', color: '#64748B', dot: '#64748B' };
  if (!isWeekend) {
    if (totalMin >= 570 && totalMin < 930) { // 9:30–15:30 ET (UTC-4 summer: +240)
      // Approximate NYSE: 13:30–20:00 UTC
      if (totalMin >= 810 && totalMin < 1200) {
        session = { label: 'NYSE Open', color: '#10B981', dot: '#10B981' };
      } else if (totalMin >= 540 && totalMin < 810) {
        session = { label: 'Pre-Market', color: '#F59E0B', dot: '#F59E0B' };
      } else {
        session = { label: 'After Hours', color: '#8B5CF6', dot: '#8B5CF6' };
      }
    }
  }

  return (
    <div className="flex items-center justify-between px-2 py-1.5 rounded bg-slate-900/60 border border-slate-800">
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: session.dot }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: session.color }}>{session.label}</span>
      </div>
      <span className="text-[10px] text-slate-600 font-mono">
        {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' })} ET
      </span>
    </div>
  );
}

const AiQInnerApp: React.FC<{ isDemoMode?: boolean }> = ({ isDemoMode = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); 
  const [isCmdPaletteOpen, setIsCmdPaletteOpen] = useState(false); 
  const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
      "Front Desk": true,
      "Research": true,
      "Quant Lab": false,
      "Risk Management": false,
      "Middle Office": false,
      "Back Office": false,
      "HK Regulatory": false,
      "Community": false,
      "Administration": false,
  });
  
  const location = useLocation();
  const navigate = useNavigate(); 
  const { notifications } = useAiQ();
  const { currentUser, userProfile, logout } = useAuth(); // Auth context for signing out
  const { activePortfolio } = usePortfolio(); // Portfolio context
  
  // Demo context - conditionally get it based on isDemoMode prop
  // Note: In demo mode, this component is wrapped with DemoProvider, so useDemo() will work
  // In regular mode, we're also wrapped with DemoProvider (isDemoMode=false), so it's always available
  const demoContext = useDemo();
  const demoData = isDemoMode ? demoContext : null;

  const unreadCount = notifications.filter(n => !n.read).length;

  const [activeView, setActiveView] = useState<ViewType>(() => {
      const saved = localStorage.getItem('aiq_active_view');
      return (saved as ViewType) || ViewType.DASHBOARD;
  });

  const [pendingTicketData, setPendingTicketData] = useState<any>(null);

  const [userHandle, setUserHandle] = useState(() => {
    return localStorage.getItem('aiq_user_handle') || 'Loading...';
  });

  // Update user handle from Firebase Auth or Demo
  useEffect(() => {
    if (isDemoMode && demoData) {
      setUserHandle(demoData.demoUser.displayName);
      localStorage.setItem('aiq_user_handle', demoData.demoUser.displayName);
    } else if (currentUser) {
      const displayName = userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
      setUserHandle(displayName);
      localStorage.setItem('aiq_user_handle', displayName);
    } else {
      setUserHandle('Guest User');
      localStorage.removeItem('aiq_user_handle');
    }
  }, [currentUser, userProfile, isDemoMode, demoData]);

  useEffect(() => {
      localStorage.setItem('aiq_active_view', activeView);
  }, [activeView]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const handle = params.get('handle');
    if (handle && handle !== 'null' && handle !== 'undefined') {
        setUserHandle(handle);
        localStorage.setItem('aiq_user_handle', handle);
    }
    // Support deep-linking to views via ?view=PUBLIC_INSIGHTS&id=xxx
    const viewParam = params.get('view');
    if (viewParam && Object.values(ViewType).includes(viewParam as ViewType)) {
        setActiveView(viewParam as ViewType);
    }
  }, [location.search]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCmdPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleGroup = (label: string) => {
      setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNavClick = (view: ViewType) => {
    setActiveView(view);
    if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
    }
  };

  // Listen for cross-view navigation from child components (e.g., insight action buttons)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'aiq_active_view' && e.newValue) {
        setActiveView(e.newValue as ViewType);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const handleTradeBooking = (ticketData: any) => {
      setPendingTicketData(ticketData);
      setActiveView(ViewType.MULTI_ASSET_OPS); 
  };

  const handleLogout = async () => {
    if (isDemoMode) {
      navigate('/login');
      toast.success('Thanks for trying the demo!');
      return;
    }

    try {
        await logout();
        navigate('/login');
        toast.success('Logged out successfully');
    } catch (e) {
        toast.error('Failed to log out');
    }
  };

  const [isLoadingTalkoruLink, setIsLoadingTalkoruLink] = useState(false);

  const handleVisitTalkoru = async () => {
    if (isDemoMode) {
      window.open('https://www.talkoru.com', '_blank');
      return;
    }

    // Open window immediately to avoid popup blocker, then navigate
    const newWindow = window.open('about:blank', '_blank');

    setIsLoadingTalkoruLink(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) {
        if (newWindow) newWindow.location.href = 'https://www.talkoru.com';
        return;
      }

      const response = await fetch(`${API_URL}/api/bridge/my_talkoru_link`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get Talkoru link');
      }

      const data = await response.json();
      if (newWindow) {
        newWindow.location.href = data.link || 'https://www.talkoru.com';
      }
    } catch (e) {
      console.error('Error getting Talkoru link:', e);
      // Fallback to regular link
      if (newWindow) newWindow.location.href = 'https://www.talkoru.com';
    } finally {
      setIsLoadingTalkoruLink(false);
    }
  };

  const goToLogin = () => navigate('/login');

  const renderContent = () => {
    // ── Access gate ──────────────────────────────────────────────────────────
    const access = VIEW_ACCESS[activeView] ?? 'public';
    const activeItem = MENU_STRUCTURE.flatMap(g => g.items).find(i => i.id === activeView);
    const viewLabel = activeItem?.label ?? activeView.replace(/_/g, ' ');

    if (!isDemoMode) {
      if (access === 'auth' && !currentUser) {
        return <LoginGate viewName={viewLabel} onLogin={goToLogin} />;
      }
      if (access === 'byok' && !currentUser) {
        return <BYOKGate viewName={viewLabel} onLogin={goToLogin} />;
      }
    }

    switch (activeView) {
      case ViewType.DASHBOARD: return <DashboardView />;
      
      // Retail Investor Views
      case ViewType.MY_PORTFOLIO: return <MyPortfolioView />;
      case ViewType.TRANSACTIONS: return <TransactionsView />;
      case ViewType.WATCHLIST: return <WatchlistView />;
      case ViewType.STOCK_ANALYSIS: return <StockAnalysisView />;
      case ViewType.NEWS_INTELLIGENCE: return <NewsIntelligenceView />;
      case ViewType.AI_CHAT: return <AiChatView />;
      case ViewType.BACKTEST_PAIRS: return <BacktestPairsView />;
      case ViewType.COMMUNITY_HUB: return <CommunityHubView />;
      case ViewType.LIVE_CHAT: return <LiveChatView />;
      
      // Institutional Views
      case ViewType.PORTFOLIO: return <PortfolioView />;
      case ViewType.QUANT_COMPUTING: return <QuantPricingView onBookQuote={handleTradeBooking} />;
      case ViewType.MARKET_DATA: return <MarketDataView />;
      case ViewType.ARCHITECTURE: return <ArchitectureView />;
      case ViewType.RISK_MANAGEMENT: return <RiskManagementView />;
      case ViewType.MULTI_ASSET_OPS: return <MultiAssetOpsView initialTicketData={pendingTicketData} onClearTicketData={() => setPendingTicketData(null)} />;
      case ViewType.IPV_CONTROL: return <IPVControlView />;
      case ViewType.STRATEGY_LAB: return <StrategyLabView />;
      case ViewType.STRUCTURING: return <StructuringView />;
      case ViewType.CRM: return <CRMView />;
      case ViewType.COLLABORATION: return <CollaborationView />; 
      case ViewType.REPORTING: return <ReportingView />;
      case ViewType.COLLATERAL: return <CollateralView />;
      case ViewType.REGULATORY: return <RegulatoryView />;
      case ViewType.TREASURY: return <TreasuryView />;
      case ViewType.RFQ_HUB: return <RFQView />;
      case ViewType.ALGO_STUDIO: return <AlgoView />;
      case ViewType.PROGRAM_TRADING: return <ProgramTradingView />;
      case ViewType.TCA: return <TCAView />;
      case ViewType.RECONCILIATION: return <ReconciliationView />;
      case ViewType.COUNTERPARTY: return <CounterpartyView />;
      case ViewType.WORKFLOW: return <WorkflowView />;
      case ViewType.ANALYTICS: return <AnalyticsView />;
      case ViewType.SCREENER: return <MarketScreenerView />;
      case ViewType.DOC_INTELLIGENCE: return <DocumentIntelligenceView onBookTrade={handleTradeBooking} />;
      case ViewType.MODEL_REGISTRY: return <ModelRegistryView />;
      case ViewType.MACRO: return <MacroView />;
      case ViewType.MARKET_BREADTH: return <MarketBreadthView />;
      case ViewType.COMPLIANCE: return <SurveillanceView />;
      case ViewType.ADMIN: return <AdminView />;

      // New Research Tools
      case ViewType.VALUATION: return <ValuationView />;
      case ViewType.EARNINGS_CALENDAR: return <EarningsCalendarView />;
      case ViewType.FACTOR_EXPOSURE: return <FactorExposureView />;

      // New Quant Lab Tools
      case ViewType.KELLY_SIZING: return <KellySizingView />;

      // Market Insights (admin only — redirect non-admins to published insights)
      case ViewType.MARKET_INSIGHTS:
        if (currentUser?.email && ADMIN_EMAILS.includes(currentUser.email)) return <InsightsView />;
        return <PublicInsightsView />;
      case ViewType.PUBLIC_INSIGHTS: return <PublicInsightsView />;
      case ViewType.INSIGHT_TRACK_RECORD: return <InsightTrackRecordView />;

      // HK Regulatory (Type 11)
      case ViewType.COUNTERPARTY_MONITOR: return <CounterpartyMonitorView />;
      case ViewType.MARGIN_DASHBOARD: return <MarginDashboardView />;
      case ViewType.CAPITAL_ADEQUACY: return <CapitalAdequacyView />;
      case ViewType.SFC_REPORTING: return <SFCReportingView />;

      default: return <DashboardView />;
    }
  };

  return (
    <div 
      id="aiq-terminal-root" 
      className="flex h-screen w-screen bg-[#F0F2F5] font-sans text-slate-900 overflow-hidden"
    >
      <style>{`
        #aiq-terminal-root ::-webkit-scrollbar { width: 6px; height: 6px; }
        #aiq-terminal-root ::-webkit-scrollbar-track { background: #0B1120; }
        #aiq-terminal-root ::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
        #aiq-terminal-root ::-webkit-scrollbar-thumb:hover { background: #475569; }
        #aiq-terminal-root button { background-color: transparent; padding: 0; border: none; color: inherit; }
        #aiq-terminal-root a { text-decoration: none; color: inherit; }
        #aiq-terminal-root ul { list-style: none; padding: 0; margin: 0; }
        #aiq-terminal-root * { box-sizing: border-box; }
        
        /* Portfolio Switcher Dark Theme */
        #portfolio-switcher-cleaner button,
        #portfolio-switcher-cleaner select {
            background: transparent !important;
            border: none !important;
            color: #94a3b8 !important;
            box-shadow: none !important;
            text-shadow: none !important;
            font-weight: 500 !important;
        }
        #portfolio-switcher-cleaner button:hover,
        #portfolio-switcher-cleaner select:hover {
            color: white !important;
            background: rgba(30, 41, 59, 0.5) !important;
        }
        #portfolio-switcher-cleaner select {
            padding: 8px !important;
            border-radius: 6px !important;
        }
      `}</style>

      {/* Demo Banner - Only show in demo mode */}
      {isDemoMode && <DemoBanner position="top" />}

      <AiQAssistant activeView={activeView} />
      <CommandPalette isOpen={isCmdPaletteOpen} onClose={() => setIsCmdPaletteOpen(false)} onNavigate={handleNavClick} />
      <NotificationPanel isOpen={isNotifPanelOpen} onClose={() => setIsNotifPanelOpen(false)} />
      <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} />
      <Toaster position="top-center" />

      {isSidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* --- TERMINAL SIDEBAR --- */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-[280px] bg-[#0B1120] text-slate-400 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out shrink-0 border-r border-slate-800
        lg:static lg:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        
        {/* Platform Header */}
        <div className="px-4 pt-4 pb-3 border-b border-slate-800/50">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shrink-0">
                    <MonitorPlay size={16} className="text-white" />
                </div>
                <div>
                    <div className="text-[15px] font-bold text-white tracking-wide leading-none">AiLabQuant</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-widest mt-0.5">Institutional Platform</div>
                </div>
            </div>
            {/* Market session status bar */}
            <MarketSessionBadge />
        </div>

        {/* Portfolio Switcher */}
        <div className="px-4 py-3 border-b border-slate-800/50">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 px-2">
            Active Portfolio
          </div>
          {isDemoMode && demoData ? (
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
              <div className="text-sm font-semibold text-white">{demoData.demoPortfolio.name}</div>
              <div className="text-xs text-slate-500 mt-1">{demoData.demoPortfolio.description}</div>
            </div>
          ) : currentUser ? (
            <>
              <div id="portfolio-switcher-cleaner" className="bg-slate-900/50 border border-slate-800 rounded-lg p-2">
                <PortfolioSwitcher />
              </div>
              {activePortfolio && (
                <div className="text-xs text-slate-500 mt-2 px-2">
                  {activePortfolio.description || 'No description'}
                </div>
              )}
            </>
          ) : (
            <button
              onClick={goToLogin}
              className="w-full text-left bg-slate-900/50 border border-slate-700/50 border-dashed rounded-lg p-3 text-xs text-slate-500 hover:text-slate-300 hover:border-slate-600 transition-colors"
            >
              Sign in to view portfolios →
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto custom-scrollbar">
          
          <div className="px-4 mb-4">
              <button 
                onClick={() => handleNavClick(ViewType.DASHBOARD)} 
                className={`w-full flex items-center px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 group ${
                  activeView === ViewType.DASHBOARD 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40' 
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span className={`mr-3 ${activeView === ViewType.DASHBOARD ? 'text-blue-200' : 'text-slate-500 group-hover:text-slate-300'}`}>
                  <IconDashboard size={20} />
                </span> 
                Dashboard
              </button>
          </div>

          <div className="space-y-0.5">
              {MENU_STRUCTURE.map(group => (
                  <div key={group.label} className="px-0 pb-1">
                      <button
                        onClick={() => toggleGroup(group.label)}
                        className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-800/30 transition-colors group"
                      >
                          <div className="flex items-center gap-2.5">
                              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                              <div className="text-left">
                                  <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wider leading-none">
                                      {group.label}
                                  </div>
                                  <div className="text-[9px] text-slate-400 tracking-wide mt-0.5">
                                      {group.sublabel}
                                  </div>
                              </div>
                          </div>
                          <span className="text-slate-600 group-hover:text-slate-400 transition-colors">
                              {expandedGroups[group.label] ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                          </span>
                      </button>

                      <div className={`overflow-hidden transition-all duration-300 ${expandedGroups[group.label] ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="mt-0.5 pb-1" style={{ borderLeft: `2px solid ${group.color}20`, marginLeft: '19px' }}>
                            {group.items.filter(item => !item.adminOnly || (currentUser?.email && ADMIN_EMAILS.includes(currentUser.email))).map(item => {
                                const isActive = activeView === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleNavClick(item.id)}
                                        className={`w-full flex items-center pl-4 pr-3 py-1.5 text-[13px] transition-colors ${
                                            isActive
                                            ? 'text-white font-medium'
                                            : 'text-slate-300 hover:text-white hover:bg-slate-800/40'
                                        }`}
                                        style={isActive ? { backgroundColor: `${group.color}18`, color: 'white' } : {}}
                                    >
                                        <span className="mr-2.5 w-4 flex justify-center shrink-0" style={{ color: isActive ? group.color : undefined }}>
                                            {React.cloneElement(item.icon as React.ReactElement, { size: 15 })}
                                        </span>
                                        <span className="truncate">{item.label}</span>
                                        {isActive && (
                                            <span className="ml-auto w-1 h-4 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                                        )}
                                    </button>
                                );
                            })}
                          </div>
                      </div>
                  </div>
              ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 bg-[#080c17] border-t border-slate-800 space-y-4">
          
          {/* User Profile */}
          <div 
            className="flex items-center p-2 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer border border-transparent hover:border-slate-700"
            onClick={() => setIsProfileModalOpen(true)}
          >
            <img 
              src={
                isDemoMode && demoData 
                  ? demoData.demoUser.photoURL 
                  : currentUser?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userHandle)}&size=36&background=3b82f6&color=fff`
              }
              alt="Profile"
              className="h-9 w-9 rounded border border-slate-600"
            />
            <div className="ml-3 overflow-hidden">
              <p className="text-sm font-bold text-slate-200 truncate">{userHandle}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${isDemoMode ? 'bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]' : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'}`}></span>
                  <p className="text-[11px] text-slate-400">{isDemoMode ? 'Demo Mode' : 'Click to edit profile'}</p>
              </div>
            </div>
          </div>

          {/* Visit Talkoru */}
          <button
            onClick={handleVisitTalkoru}
            disabled={isLoadingTalkoruLink}
            className="w-full flex items-center justify-center py-2.5 border border-purple-700/50 rounded-lg text-xs font-bold text-purple-400 hover:text-purple-300 hover:bg-purple-900/30 hover:border-purple-500/50 transition-all group gap-2"
          >
             <IconGlobeAlt />
             <span>{isLoadingTalkoruLink ? 'Opening...' : 'Visit Talkoru'}</span>
          </button>

          {/* WhatsApp Access */}
          <a
            href="https://wa.me/14155238886?text=Hi%20QuantClaw"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center py-2.5 border border-green-700/50 rounded-lg text-xs font-bold text-green-400 hover:text-green-300 hover:bg-green-900/30 hover:border-green-500/50 transition-all group gap-2"
          >
             <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
               <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
             </svg>
             <span>Chat via WhatsApp</span>
          </a>

          {/* Sign In / Sign Out */}
          {currentUser || isDemoMode ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center py-2.5 border border-slate-700/50 rounded-lg text-xs font-bold text-slate-400 hover:text-red-400 hover:bg-slate-800 hover:border-slate-500 transition-all group gap-2"
            >
               <LogOut size={14} />
               <span>Sign Out</span>
            </button>
          ) : (
            <button
              onClick={goToLogin}
              className="w-full flex items-center justify-center py-2.5 border border-blue-700/50 rounded-lg text-xs font-bold text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 hover:border-blue-500/50 transition-all group gap-2"
            >
               <LogIn size={14} />
               <span>Sign In</span>
            </button>
          )}
        </div>
      </aside>

      {/* --- MAIN CONTENT AREA --- */}
      <main className={`flex-1 flex flex-col overflow-hidden relative z-0 bg-[#F0F2F5] ${isDemoMode ? 'pt-[60px]' : ''}`}>
        
        {/* Toolbar Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex justify-between items-center px-6 z-10 shrink-0 shadow-sm">
           <div className="flex items-center gap-3">
               <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden text-slate-500 hover:text-slate-800 focus:outline-none"><IconMenu /></button>
               {(() => {
                 const activeGroup = MENU_STRUCTURE.find(g => g.items.some(i => i.id === activeView));
                 const activeItem = activeGroup?.items.find(i => i.id === activeView);
                 return (
                   <div className="flex items-center gap-2">
                     {activeGroup && (
                       <>
                         <span className="text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{ color: activeGroup.color, backgroundColor: `${activeGroup.color}15` }}>
                           {activeGroup.label}
                         </span>
                         <span className="text-slate-300">/</span>
                       </>
                     )}
                     <h2 className="text-base font-bold text-slate-800">
                       {activeItem?.label || activeView.replace(/_/g, ' ')}
                     </h2>
                   </div>
                 );
               })()}
           </div>
           
           <div className="flex items-center space-x-4">
             <div className="hidden md:flex items-center gap-1.5 text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                LIVE DATA
             </div>
             
             <div className="h-5 w-px bg-slate-300 mx-2"></div>

             <button onClick={() => setIsCmdPaletteOpen(true)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-md px-3 py-1.5">
               <IconCommand size={14} /><span className="text-xs font-bold font-mono hidden sm:inline text-slate-600">CMD+K</span>
             </button>
             
             <button onClick={() => setIsNotifPanelOpen(true)} className="relative p-2 text-slate-400 hover:text-blue-600 transition-colors rounded-full hover:bg-slate-100">
               <IconBell size={20} />
               {unreadCount > 0 && <span className="absolute top-1.5 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>}
             </button>
           </div>
        </header>

        {/* View Viewport */}
        <div className="flex-1 overflow-hidden relative bg-[#F0F2F5]">
          {renderContent()}
        </div>

        {/* Ticker Footer */}
        <LiveTicker />
      </main>
    </div>
  );
}

const App: React.FC<{ isDemoMode?: boolean }> = ({ isDemoMode = false }) => {
  return (
    <AiQProvider>
      <AiQInnerApp isDemoMode={isDemoMode} />
    </AiQProvider>
  );
};

export default App;